import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Footer from './components/Footer';

type Sponsor = { name: string; logo: string; url?: string };

/**
 * Sponsors, in two tiers. Main sponsors get their own row at a larger size;
 * the rest sit below at the standard size. To promote or demote a sponsor,
 * move its entry between the two arrays -- nothing else needs to change.
 */
const MAIN_SPONSORS: Sponsor[] = [
    // TODO: add GASixty's website once we have it, and the logo becomes a link.
    { name: 'GASixty', logo: '/media/logo-gasixty.png' },
    {
        name: 'MacPro Panel Beaters',
        logo: '/media/macpro_panel_beaters_sponsor.jpg',
        url: 'https://www.macpropanelbeaters.co.za/',
    },
];

const SPONSORS: Sponsor[] = [
    { name: 'Hytera', logo: '/media/logo-hytera.png', url: 'https://hytera.co.za/' },
    { name: 'MST', logo: '/media/logo-mst-2.jpeg', url: 'https://mstsa.co.za/' },
    { name: 'The Blades', logo: '/media/logo-the-blades-black.png', url: 'https://www.theblades.co.za/' },
    { name: 'Build It', logo: '/media/logo-build-it.png', url: 'https://www.buildit.co.za/' },
    { name: 'Q Carwash', logo: '/media/logo-q-carwash.png', url: 'https://qscarwash.co.za/' },
];

const ASSOCIATES: Sponsor[] = [
    { name: 'APA', logo: '/media/logo-apa.jpeg', url: 'https://apaems.mystrikingly.com/' },
];

const SponsorCard: React.FC<{ sponsor: Sponsor }> = ({ sponsor }) => {
    const logo = <img src={sponsor.logo} alt={sponsor.name} />;
    return (
        <div className="sponsor-card">
            {sponsor.url ? <a href={sponsor.url}>{logo}</a> : logo}
        </div>
    );
};

const Home: React.FC = () => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [videoModalOpen, setVideoModalOpen] = useState(false);
    const location = useLocation();

    const carouselSlides = [
        {
            title: 'Wildfire Response',
            url: 'media/14.jpeg'
        },
        {
            title: 'Wildfire Response',
            url: 'media/4.jpeg'
        },
        {
            title: 'Wildfire Response',
            url: 'media/15.jpeg'
        },
        {
            title: 'Wildfire Response',
            url: 'media/7.jpeg'
        },
        {
            title: 'Wildfire Response',
            url: 'media/8.jpeg'
        },
        {
            title: 'Wildfire Response',
            url: 'media/9.jpeg'
        },
        {
            title: 'Wildfire Response',
            url: 'media/10.jpeg'
        },
        {
            title: 'Wildfire Response',
            url: 'media/11.jpeg'
        },
        {
            title: 'Wildfire Response',
            url: 'media/12.jpeg'
        },
    ];

    const testimonials = [
        {
            testament: "Kilo Delta On Call assisted us during an incident. One call to them and everything was arranged and organised. They arrived quickly and immediately provided support in a professional and sincere manner. Their follow-up and continued assistance long after the incident gave me real peace of mind. Kilo Delta On Call team, you are exceptional.",
            author: "Louis van Wyk",
            image: "/media/testimonial-1.jpeg"
        },
        {
            testament: "Ek wil net vir Milydia en John van Kilo Delta on Call uit die diepte van my hart uit baie dankie sê vir die spoedige en professionele hulp met n bevalling wat ons op ons plot in Leeuwfontein gehad het. Ek het nie geweet wat om te doen nie en ek het op zello ingeroep en hulle was binne 10 minute by my. Julle is werklik aardse engele. Ek dink regtig as dit nie vir julle was nie weet ek nie of Mamma en baba dit sou maak nie. Dit is wonderlik om te weet ons het sulke goeie mense in ons gemeenskap. Weereens baie dankie",
            author: "Hannes Swift",
            image: "/media/testimonial-2.jpeg"
        },
    ];

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % carouselSlides.length);
        }, 5000);
        return () => clearInterval(interval);
    }, []);

    // Handle scrolling to section if navigated from another page
    useEffect(() => {
        if (location.state && location.state.scrollTo) {
            const sectionId = location.state.scrollTo;
            setTimeout(() => {
                scrollToSection(sectionId);
            }, 100); // Small delay to ensure the page has loaded
        }
    }, [location]);

    // Close video modal when clicking outside
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setVideoModalOpen(false);
            }
        };

        if (videoModalOpen) {
            document.addEventListener('keydown', handleEscape);
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = 'unset';
        };
    }, [videoModalOpen]);

    const moveCarousel = (direction: number) => {
        setCurrentSlide((prev) => {
            const newSlide = prev + direction;
            if (newSlide < 0) return carouselSlides.length - 1;
            if (newSlide >= carouselSlides.length) return 0;
            return newSlide;
        });
    };

    const scrollToSection = (sectionId: string) => {
        const element = document.getElementById(sectionId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
            setMobileMenuOpen(false);
        }
    };

    return (
        <div className="font-sans text-gray-800">

            {/* Hero Banner */}
            <section id="home" className="hero-banner">

                <img src="/media/hero-banner.jpg" alt="Emergency Response Team" className="hero-banner-image" />
                <div className="hero-content">
                    <img alt="Kilo Delta Logo" src="/media/logo-full.png" className="logo-hero-banner"/>
                    <h1>FIRST RESPONDERS</h1>
                    <h2>SAVING LIVES TOGETHER</h2>
                </div>
            </section>

            {/* About */}
            <section id="about" className="about-section">
                <div className="about-content">
                    <h2>EMERGENCY SUPPORT WHEN IT MATTERS MOST</h2>
                    <p>KILO DELTA ON CALL NPO is a dedicated non-profit organization delivering comprehensive medical, fire, search & rescue, and trauma support services. While we are not a licensed EMS provider, our trained  volunteers plays a critical role in our community. As a volunteer-led emergency response team, we provide rapid, no-cost assistance to communities particularly rural and farming areas where formal services are limited or experience extended response times.  Our mission is to ensure that every call for help receives a timely, professional, and compassionate response.</p>
                    {/*<Link to="/about" className="learn-more-button">Learn More About Us</Link>*/}
                </div>
            </section>

            {/* Team Info Block */}
            <section className="team-info-block">
                <div className="team-info-content">
                    <div className="team-info-image" style={{ display: 'flex' }}>
                        <img src="/media/team_3.jpeg" alt="Our Team" />
                    </div>
                    <div className="team-info-text">
                        <h2>Meet The Team</h2>
                        <p>We are a dedicated group of volunteers committed in serving our community in emergency situations. Our team operates 24/7, providing critical support when it matters most. With extensive training and a passion for helping others, we bridge the gap between emergency situations and professional medical services, especially in outlying and farming communities.</p>
                    </div>
                </div>
            </section>

            {/* Video Section

//             <section className="video-section">
//                 <div className="section-header">
//                     <h2>Watch Our Story</h2>
//                     <p>See the dedication and commitment behind our emergency response team</p>
//                 </div>
//                 <div className="video-container">
//                     <div className="video-thumbnail" onClick={() => setVideoModalOpen(true)}>
//                         <img src="/media/video-thumbnail.jpeg" alt="Play video" />
//                         <div className="play-button">
//                             <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
//                                 <circle cx="40" cy="40" r="40" fill="rgba(255, 255, 255, 0.9)" />
//                                 <path d="M32 26L56 40L32 54V26Z" fill="#dc2626" />
//                             </svg>
//                         </div>
//                     </div>
//                 </div>
//             </section>
*/}
            {/* Video Modal
//             {videoModalOpen && (
//                 <div className="video-modal" onClick={() => setVideoModalOpen(false)}>
//                     <div className="video-modal-content" onClick={(e) => e.stopPropagation()}>
//                         <button className="video-modal-close" onClick={() => setVideoModalOpen(false)}>
//                             ✕
//                         </button>
//                         <div className="video-wrapper">
//                             <video
//                                 controls
//                                 autoPlay
//                                 className="modal-video"
//                             >
//                                 <source src="/media/video-kameeldrift.mp4" type="video/mp4" />
//                                 Your browser does not support the video tag.
//                             </video>
//                         </div>
//                     </div>
//                 </div>
//             )}
*/}
            {/* Services */}
            <section id="services" className="services-section">
                <div className="section-header">
                    <h2>Our Primary Services</h2>
                    <p>Providing rapid first response to communities in need until professional services arrive</p>
                </div>
                <div className="services-grid">
                    <div className="service-card">
                        <h3>Medical Response</h3>
                        <p>Rapid response provided by qualified first responders. We provide initial care and stabilization when every second counts, bridging the gap until provincial or private ambulance services arrive at the scene.</p>
                    </div>
                    <div className="service-card">
                        <h3>Search & Rescue</h3>
                        <p>Search and rescue operations from missing persons, to disaster response. Our trained teams use advanced equipment and techniques to locate and safely extract individuals in distress across challenging terrain, alongside other search and rescue services .</p>
                    </div>
                    <div className="service-card">
                        <h3>Fire Suppression</h3>
                        <p>Our trained firefighters respond rapidly to veld fires, structural fires and vehicle fires to protect lives, property, and livestock in rural and farming communities bridging the gap before provincial fire services arrive.</p>
                    </div>
                    <div className="service-card">
                        <h3>Trauma Support</h3>
                        <p>Our dedicated team of trauma support volunteers and joint organisations are always ready to lend a hand, from assisting at accident seances to specialises one on one counselling.</p>
                    </div>
                </div>
            </section>

            {/* Donations */}
            <section id="donate" className="donations-section">
                <div className="donations-content">
                    <div className="donations-text">
                        <h2>Support Our Life-Saving Mission</h2>
                        <p>Your contribution will make a direct and measurable difference. Whether through a once-off donation or ongoing support, you will be helping us save lives and protect communities where help is often hours away.</p>
                        <p>To continue providing this essential public service, we need your financial support. Through the dedication of our volunteers and the generosity of businesses and individuals like yourself, we have been able to respond to emergencies that save lives, protect property, and bring hope to communities and families in their most critical times of need</p>

                        <Link to="/donate" className="donate-button">Donate Now</Link>

                        <div className="donation-amounts">
                            <h4>Prefer a direct EFT?</h4>
                            <ul>
                                <li><strong>Account Name:</strong> KILO DELTA ON CALL</li>
                                <li><strong>Bank:</strong> First National Bank</li>
                                <li><strong>Account Number:</strong> 63177193827</li>
                                <li><strong>Branch Code:</strong> 251037</li>
                                <li><strong>Reference:</strong> Donation (Your Name / Company)</li>
                            </ul>
                        </div>

                        <p className="donation-contact-note">
                            Donating on behalf of a company, or want to discuss sponsorship?{' '}
                            <a href="mailto:info@kilodeltaoncall.org">Get in touch with us</a>.
                        </p>
                    </div>

                    <div className="testimonials-card">
                        <h3>Community Testimonials</h3>
                        <p>Read what our community members have to say about our services:</p>

                        <div className="testimonials-container">
                            {testimonials.map((testimonial, index) => (
                                <div key={index} className="testimonial-item">
                                    <div className="testimonial-header">
                                        <div className="testimonial-image">
                                            <img src={testimonial.image} alt={`${testimonial.author} portrait`} />
                                        </div>
                                        <p className="testimonial-author">  {testimonial.author}</p>
                                    </div>
                                    <p className="testimonial-text">"{testimonial.testament}"</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>


            {/* Carousel */}
            <section className="carousel-section">
                <div className="section-header">
                    <h2>See Us In Action</h2>
                    <p>See how we serve our communities every day</p>
                </div>
                <div className="carousel-container">
                    <div className="carousel-wrapper">
                        <div className="carousel-slides" style={{ transform: `translateX(-${currentSlide * 100}%)` }}>
                            {carouselSlides.map((slide, index) => (
                                <div
                                    key={index}
                                    className="carousel-slide"
                                >
                                    <div className="carousel-slide-content">

                                        <div className="carousel-image-container">
                                            <div
                                                className="carousel-slide-bg"
                                                style={{
                                                    backgroundImage: `url(${slide.url})`,
                                                }}
                                            ></div>
                                            <img src={slide.url} alt={slide.title}/>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <button className="carousel-nav prev" onClick={() => moveCarousel(-1)}>‹</button>
                        <button className="carousel-nav next" onClick={() => moveCarousel(1)}>›</button>
                    </div>
                    <div className="carousel-dots">
                        {carouselSlides.map((_, index) => (
                            <button
                                key={index}
                                className={`carousel-dot ${currentSlide === index ? 'active' : ''}`}
                                onClick={() => setCurrentSlide(index)}
                            />
                        ))}
                    </div>
                </div>
            </section>

            {/* Sponsors */}
            <section id="sponsors" className="sponsor-section">
                <div className="section-header">
                    <h2>Our Sponsors</h2>
                    <p>A big shoutout to our Sponsors</p>
                </div>
                <div className="sponsor-grid sponsor-grid-main">
                    {MAIN_SPONSORS.map((sponsor) => (
                        <SponsorCard key={sponsor.name} sponsor={sponsor} />
                    ))}
                </div>
                <div className="sponsor-grid">
                    {SPONSORS.map((sponsor) => (
                        <SponsorCard key={sponsor.name} sponsor={sponsor} />
                    ))}
                </div>

                <div className="section-header">
                    <h2>In Association With </h2>
                </div>
                <div className="sponsor-grid">
                    {ASSOCIATES.map((sponsor) => (
                        <SponsorCard key={sponsor.name} sponsor={sponsor} />
                    ))}
                </div>
                <hr style={{width: '50%', margin: '20px auto',marginBottom: '50px' }}/>
                <div  className="section-header">
                    <img src="/media/logo-partners.png" alt="Our Partners" style={{ width: '400px'}}  className="volunteers-image" />
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default Home;